/**
 * Рецепт — плоский список ингредиентов, в один уровень.
 *
 * Распознавание называет блюдо целиком: «Плов, 350 г», а раскладку — рис,
 * морковь, мясо — кладёт внутрь этой позиции. Для дневника это правильно,
 * карточка должна говорить «Плов», а не восемь строк. Но экран рецепта устроен
 * в один уровень: там и правка веса, и замена продукта, и сумма веса блюда
 * считаются по плоскому списку — и сохранение отправляет плоский список тоже.
 *
 * Из-за этого «Плов» из Избранного открывался рецептом из одного ингредиента
 * «Плов» без раскладки, а сохранение записывало этот единственный ингредиент
 * в блюдо, стирая раскладку насовсем: PATCH /cooking/dish/:id собирает
 * ingredients из присланных items, вложенности там нет.
 *
 * Разворачиваем при загрузке: позиция с раскладкой заменяется своими
 * ингредиентами. Средний уровень на этом экране всё равно ничего не сообщает —
 * название блюда стоит в заголовке, — а рецепт становится тем же, чем его
 * делает создание с нуля: списком продуктов.
 *
 * Разворот живёт здесь, а не в API: в дневнике те же данные нужны с обоими
 * уровнями, там «Плов» раскрывается по нажатию.
 */

import { distributeWeights } from './weights.js'

export function flattenRecipeIngredients(items) {
  if (!Array.isArray(items)) return items

  const flat = []
  for (const item of items) {
    const subs = item.ingredients
    if (!Array.isArray(subs) || subs.length === 0) {
      flat.push(item)
      continue
    }

    // Веса раскладки приводим к весу позиции. У блюд, сохранённых до того, как
    // состав начал ходить за весом позиции, они могут не сходиться — и тогда
    // рецепт получил бы вес и калорийность, которых у блюда нет.
    const weights = distributeWeights(
      subs.map(s => Number(s.weight_g) || 0),
      Number(item.weight_g) || 0
    )

    subs.forEach((sub, i) => {
      const weight = weights[i]
      const per100 = sub.per100 || {}
      // Клетчатку не подменяем нулём: её у продукта может не быть вовсе, и ноль
      // неотличим от настоящего нуля — на нём сумма по блюду соврёт.
      const total = (key) => (per100[key] != null
        ? Math.round(per100[key] * weight / 100 * 10) / 10
        : null)

      flat.push({
        product_name: sub.name,
        db_product_name: sub.db_name || null,
        product_id: sub.product_id || null,
        product_source: sub.source || null,
        product_nutrients: sub.nutrients || null,
        weight_g: weight,
        per100,
        kcal:    total('kcal') || 0,
        protein: total('protein') || 0,
        fat:     total('fat') || 0,
        carbs:   total('carbs') || 0,
        fiber:   total('fiber'),
        water:   total('water'),
        vitamins: sub.vitamins || null,
        grain: sub.grain || null,
        cooking_method: item.cooking_method || undefined,
      })
    })
  }
  return flat
}
